/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/react";
import { changeStyleWithHosting } from "utility/hosting";

import type { FC } from "react";

type Props = {
  ariaLabel: string;
  currentStepId: string;
  steps: readonly {
    id: string;
    label: string;
  }[];
};

export const Stepper: FC<Props> = ({ ariaLabel, currentStepId, steps }) => {
  const classes = styles();
  const currentStepIndex = steps.findIndex((step) => step.id === currentStepId);

  if (currentStepIndex === -1) {
    throw new Error(`Unknown current step ID: ${currentStepId}`);
  }

  return (
    <nav aria-label={ariaLabel}>
      <ol css={classes.root}>
        {steps.map((step, index) => {
          const stepStatus =
            index < currentStepIndex
              ? "completed"
              : index === currentStepIndex
                ? "current"
                : "upcoming";

          return (
            <li
              key={step.id}
              css={classes.step}
              data-status={stepStatus}
              aria-current={stepStatus === "current" ? "step" : undefined}
            >
              <span css={classes.number}>
                {stepStatus === "completed" ? "✓" : index + 1}
              </span>
              <span css={classes.label}>{step.label}</span>
            </li>
          );
        })}
      </ol>
    </nav>
  );
};

const styles = () => {
  const { accentColor } = changeStyleWithHosting().baseStyles;

  return {
    root: css`
      display: flex;
      align-items: center;
      padding: 0;
      margin: 0;
      list-style: none;
    `,
    step: css`
      display: flex;
      flex: 1;
      align-items: center;
      min-width: 0;
      color: #767676;
      font-size: 12px;
      line-height: 1.5;
      &[data-status="current"] {
        color: #222;
        font-weight: bold;
      }
      &:not(:last-of-type)::after {
        content: "";
        flex: 1;
        height: 1px;
        margin: 0 10px;
        background-color: #ddd;
      }
      &[data-status="completed"]::after {
        background-color: ${accentColor};
      }
      &:last-of-type {
        flex: none;
      }
    `,
    number: css`
      display: inline-flex;
      flex: none;
      justify-content: center;
      align-items: center;
      width: 20px;
      height: 20px;
      margin-right: 6px;
      font-size: 11px;
      font-weight: normal;
      background-color: #fff;
      border: 1px solid #ccc;
      border-radius: 50%;
      li[data-status="current"] &,
      li[data-status="completed"] & {
        color: #fff;
        background-color: ${accentColor};
        border-color: ${accentColor};
      }
    `,
    label: css`
      white-space: nowrap;
    `,
  };
};
